import { useState, useRef, useEffect, useId, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Search } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useAnchoredMenu } from './useAnchoredMenu'
import { useEscapeLayer } from './escapeLayer'
import { listboxMove } from './listboxNav'
import { SearchableOptionList } from './SearchableOptionList'
import { filterAndGroup } from './searchableFilter'

function renderLabel(text: string) {
  const trimmed = text.trimEnd()
  if (!trimmed.endsWith('*')) return text
  return (
    <>
      {trimmed.slice(0, -1).trimEnd()}
      <span className="ml-0.5 text-red-500">*</span>
    </>
  )
}

// Generic searchable dropdown with optional grouping and "top items" section.
// Built from scratch (no headless-ui dep) - small click-outside handler + filter.
// Dropdown portals to document.body so it is never clipped by overflow:hidden ancestors.

export interface SearchableOption<TValue = string> {
  value: TValue
  label: string
  hint?: string // Right-aligned secondary text (e.g. "+977", "UTC+5:45")
  prefix?: string // Left-aligned prefix (e.g. flag emoji "🇳🇵")
  group?: string // Group label - items with the same group are clustered
  searchTokens?: string // Extra search-only text (e.g. dial code, alpha-2)
}

interface Props<TValue extends string> {
  value: TValue | null
  onChange: (value: TValue) => void
  options: SearchableOption<TValue>[]
  topOptions?: SearchableOption<TValue>[] // Surfaced at top under "Most used"
  placeholder?: string
  searchPlaceholder?: string
  label?: string
  error?: string
  disabled?: boolean
  className?: string
  // Optional renderer for the trigger button content. If omitted, shows
  // selected option's prefix + label + hint.
  renderTrigger?: (selected: SearchableOption<TValue> | undefined) => React.ReactNode
  /** Lets a `<label htmlFor>` name the trigger. */
  id?: string
  required?: boolean
  /** Accessible name when no visible `label` is passed. */
  ariaLabel?: string
}

export function SearchableSelect<TValue extends string = string>({
  value,
  onChange,
  options,
  topOptions,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  label,
  error,
  disabled,
  className,
  renderTrigger,
  id,
  required,
  ariaLabel,
}: Props<TValue>) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(-1)
  const baseId = useId()
  const triggerId = id ?? `${baseId}-trigger`
  const listId = `${baseId}-listbox`
  const errorId = `${baseId}-error`
  const optionId = (i: number) => `${baseId}-opt-${i}`
  const containerRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const dropRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selected = options.find((o) => o.value === value)

  const dropPos = useAnchoredMenu({
    open,
    anchorRef: btnRef,
    menuRef: dropRef,
    listRef,
    revision: query,
    maxListHeight: 256,
  })

  // Click outside to close
  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      const t = e.target as Node
      if (
        btnRef.current &&
        !btnRef.current.contains(t) &&
        dropRef.current &&
        !dropRef.current.contains(t)
      )
        setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Focus search on open
  useEffect(() => {
    if (open) inputRef.current?.focus()
    else setQuery('')
  }, [open])

  // Escape closes the list only (not a dialog around it) and returns focus to
  // the trigger.
  useEscapeLayer(open, () => {
    setOpen(false)
    btnRef.current?.focus()
  })

  function handleOpen() {
    if (disabled) return
    setOpen((v) => !v)
  }

  function onTriggerKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (disabled || open) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      setOpen(true)
    }
  }

  const { filteredTop, groups } = filterAndGroup(options, topOptions, query)

  // Every visible row in render order, so the arrow keys walk the list the
  // way it reads (most-used first, then each group).
  const flat: SearchableOption<TValue>[] = [...filteredTop, ...groups.flatMap((g) => g.items)]
  const activeIndex = active >= 0 && active < flat.length ? active : -1

  function pick(o: SearchableOption<TValue>) {
    onChange(o.value)
    setOpen(false)
    btnRef.current?.focus()
  }

  function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      const o = flat[activeIndex] ?? (flat.length === 1 ? flat[0] : undefined)
      if (o) pick(o)
      return
    }
    if (e.key === 'Tab') {
      setOpen(false)
      return
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    const moved = listboxMove(flat, activeIndex, e.key)
    if (moved === null || moved < 0) return
    e.preventDefault()
    setActive(moved)
    document.getElementById(optionId(moved))?.scrollIntoView?.({ block: 'nearest' })
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)} ref={containerRef}>
      {label && (
        <label htmlFor={triggerId} className="text-sm font-medium text-gray-700">
          {renderLabel(label)}
        </label>
      )}
      <div className="relative">
        <button
          ref={btnRef}
          id={triggerId}
          type="button"
          // Select-only combobox, matching DropdownSelect (QA6 OPS2-20).
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-label={label ? undefined : ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-required={required || (label?.trimEnd().endsWith('*') ?? false) || undefined}
          aria-describedby={error ? errorId : undefined}
          disabled={disabled}
          onClick={handleOpen}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            'w-full h-9 px-3.5 rounded-lg bg-white border text-sm text-gray-900 flex items-center justify-between gap-2 transition-shadow duration-150',
            'focus:outline-none focus:border-brand-orange focus:shadow-input-focus',
            error
              ? 'border-red-400 focus:border-red-400 focus:shadow-none'
              : open
                ? 'border-brand-orange shadow-input-focus'
                : 'border-gray-200 hover:border-gray-300',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          {renderTrigger ? (
            renderTrigger(selected)
          ) : selected ? (
            <span className="flex items-center gap-2 truncate">
              {selected.prefix && <span className="shrink-0">{selected.prefix}</span>}
              <span className="truncate">{selected.label}</span>
              {selected.hint && <span className="text-gray-400 font-normal">{selected.hint}</span>}
            </span>
          ) : (
            <span className="text-gray-400 font-normal">{placeholder}</span>
          )}
          <ChevronDown
            size={13}
            className={cn('text-gray-400 shrink-0 transition-transform', open && 'rotate-180')}
          />
        </button>

        {open &&
          createPortal(
            <div
              ref={dropRef}
              className="fixed z-[9999] rounded-xl border border-gray-100 bg-white shadow-lg overflow-hidden"
              style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width }}
            >
              {/* Search */}
              <div className="px-3 py-2 border-b border-gray-100">
                <div className="relative">
                  <Search
                    size={12}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value)
                      setActive(-1)
                    }}
                    onKeyDown={onSearchKeyDown}
                    placeholder={searchPlaceholder}
                    role="combobox"
                    aria-label={searchPlaceholder.replace(/…|\.\.\./g, '').trim() || 'Search'}
                    aria-expanded="true"
                    aria-controls={listId}
                    aria-autocomplete="list"
                    aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
                    className="w-full h-8 pl-7 pr-3 rounded-lg bg-gray-50 text-xs focus:outline-none focus:bg-white focus:ring-2 focus:ring-brand-orange/20"
                  />
                </div>
              </div>

              <SearchableOptionList
                listRef={listRef}
                listId={listId}
                ariaLabel={label ?? ariaLabel ?? placeholder}
                maxHeight={dropPos.listMaxHeight}
                top={filteredTop}
                groups={groups}
                value={value}
                activeIndex={activeIndex}
                optionId={optionId}
                onPick={pick}
              />
            </div>,
            document.body,
          )}
      </div>
      {error && (
        <p id={errorId} className="text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
